'use client';

import {
	EventMap,
	OrderAction,
	OrderActionRecord,
	WrappedEvent,
} from '@velocity-exchange/sdk';
import React, {
	PropsWithChildren,
	useContext,
	useEffect,
	useState,
} from 'react';
import { Observable, Subject } from 'rxjs';
import { VelocityBlockchainEventSubscriberContext } from './velocityBlockchainEventSubscriberProvider';
import { Commitment } from '@solana/web3.js';
import { ENUM_UTILS, dedupeSubject } from '@velocity-exchange/common';

// This flag allows extra robustness by providing redundancy by forwarding global action events to the strict current-user events subject, and vice-versa. It requires deduplication because otherwise in the case where the data sources are healthy there will be duplicate events.
export const MERGE_USER_AND_GLOBAL_EVENT_SUBJECTS = true;
const DEDUPE_WINDOW_SIZE = 50;

/**
 * This provider is responsible for creating the subjects that the velocity events can be piped into.
 */

type VelocityBlockchainEventSubjectContextType = {
	userEventSubject: Subject<WrappedEvent<keyof EventMap>> | undefined;
	userEventObservable: Observable<WrappedEvent<keyof EventMap>> | undefined;
	globalFillEventSubject:
		| Subject<WrappedEvent<'OrderActionRecord'>>
		| undefined;
	globalFillEventObservable:
		| Observable<WrappedEvent<'OrderActionRecord'>>
		| undefined;
	userEventSubjectCommitment: Commitment | undefined;
	globalFillEventSubjectCommitment: Commitment | undefined;
};

const INITIAL_CONTEXT_VALUE: VelocityBlockchainEventSubjectContextType = {
	userEventSubject: undefined,
	userEventObservable: undefined,
	globalFillEventSubject: undefined,
	globalFillEventObservable: undefined,
	userEventSubjectCommitment: undefined,
	globalFillEventSubjectCommitment: undefined,
};

/**
 * userEventSubscriber: EventSubscriber for the current user's events
 * globalFillEventSubscriber: EventSubscriber for market-wide order action events
 */
export const VelocityBlockchainEventSubjectContext =
	React.createContext<VelocityBlockchainEventSubjectContextType>(
		INITIAL_CONTEXT_VALUE
	);

const GLOBAL_FILL_EVENT_SUBJECT = new Subject<
	WrappedEvent<'OrderActionRecord'>
>();
const USER_EVENT_SUBJECT = new Subject<WrappedEvent<keyof EventMap>>();

export const VelocityBlockchainEventSubjectProvider = (
	props: PropsWithChildren<any>
) => {
	const eventSubscriberContext = useContext(
		VelocityBlockchainEventSubscriberContext
	);
	const [contextValue, setContextValue] =
		useState<VelocityBlockchainEventSubjectContextType>(INITIAL_CONTEXT_VALUE);

	useEffect(() => {
		let userEventObservable = USER_EVENT_SUBJECT.asObservable();

		let globalFillEventObservable = GLOBAL_FILL_EVENT_SUBJECT.asObservable();

		// Apply the sliding window deduplication to the user event subject if we're merging the user events with the global order action events
		if (MERGE_USER_AND_GLOBAL_EVENT_SUBJECTS) {
			userEventObservable = dedupeSubject(
				USER_EVENT_SUBJECT,
				DEDUPE_WINDOW_SIZE,
				(event) => {
					const isFillEvent =
						event.eventType === 'OrderActionRecord' &&
						ENUM_UTILS.match(
							(event as OrderActionRecord).action,
							OrderAction.FILL
						);

					return `${event.eventType}::${event.txSig}::${
						isFillEvent
							? ((event as OrderActionRecord).fillRecordId?.toString() ?? '') // order fill events doesn't include the txSigIndex from either dlob server or trades server (can't rmb which)
							: event.txSigIndex
					}`;
				}
			);

			globalFillEventObservable = dedupeSubject(
				GLOBAL_FILL_EVENT_SUBJECT,
				DEDUPE_WINDOW_SIZE,
				(event) => {
					return `${event.eventType}::${event.txSig}::${
						event.fillRecordId?.toString() ?? ''
					}::${ENUM_UTILS.toStr(event.actionExplanation)}`;
				}
			);
		}

		const userEventSubscription = userEventObservable.subscribe();
		const globalFillEventSubscription = globalFillEventObservable.subscribe();

		setContextValue({
			userEventSubject: USER_EVENT_SUBJECT,
			userEventObservable,
			globalFillEventSubject: GLOBAL_FILL_EVENT_SUBJECT,
			globalFillEventObservable: globalFillEventObservable,
			userEventSubjectCommitment: eventSubscriberContext.commitment,
			globalFillEventSubjectCommitment: eventSubscriberContext.commitment,
		});

		return () => {
			userEventSubscription.unsubscribe();
			globalFillEventSubscription.unsubscribe();
		};
	}, [eventSubscriberContext]);

	return (
		<VelocityBlockchainEventSubjectContext value={contextValue}>
			{props.children}
		</VelocityBlockchainEventSubjectContext>
	);
};
